import * as THREE from "three";
import { prepareMaterialsForRender } from "../../materials/prepareMaterialsForRender";
import type { WebGLExtendedCapabilities } from "../../WebGLExtendedCapabilities";
import { defaultLightProbeGenerationParams } from "./LightProbeGenerationParams";
import { RenderTargetPool } from "./RenderTargetPool";
import type { RenderImageOptions } from "./SSFXRendererParams";
import { type RendererParams } from "./SSFXRendererParams";
import type SSFXRendererScene from "./SSFXRendererScene";
import { SSFXRenderTarget } from "./SSFXRenderTarget";

export const RENDER_IMAGE_DEFAULT_WIDTH = 256;
const MAX_LIGHT_PROBE_RENDER_STEPS = 2000;

// The caller is intended to read the image from HTML canvas.
export type ImageReadyCallback = (image: HTMLCanvasElement) => void;

export class SSFXImageRenderer {
  private readonly offScreenRenderTarget: SSFXRenderTarget;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly renderTargetPool: RenderTargetPool;
  readonly onScreenRenderingDisabled: boolean;

  constructor({
    renderer,
    capabilities,
    onScreenRenderingDisabled,
  }: {
    renderer: THREE.WebGLRenderer;
    capabilities: WebGLExtendedCapabilities;
    onScreenRenderingDisabled: boolean;
  }) {
    this.renderer = renderer;
    this.onScreenRenderingDisabled = onScreenRenderingDisabled;

    this.renderTargetPool = new RenderTargetPool(renderer);

    this.offScreenRenderTarget = new SSFXRenderTarget(renderer, this.renderTargetPool, 128, 128, capabilities);
  }

  renderImageToTexture(
    scene: SSFXRendererScene,
    camera: THREE.Camera,
    renderParams: RendererParams,
    options: Partial<RenderImageOptions>,
  ) {
    const imageGenerator = this.renderImageInternal(scene, camera, renderParams, options);
    let genReturn: IteratorResult<any, any>;
    do {
      genReturn = imageGenerator.next();
    } while (!genReturn.done);

    const { width, height } = this.offScreenRenderTarget.getFinalRenderSize();
    const renderTarget = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false,
      colorSpace: THREE.SRGBColorSpace,
      samples: 0,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      generateMipmaps: true,
    });
    this.offScreenRenderTarget.renderSSAAOutput(renderTarget);
    // It's important to dispose the whole render target, otherwise the texture will be kept alive by the render target.
    return {
      texture: renderTarget.texture,
      freeMemory: () => {
        renderTarget.dispose();
      },
    };
  }

  /**
   * @param scene Scene to render.
   * @param camera Camera used to render.
   * @param options Options that can be used to override current render settings, as well as setting the size of the returned image.
   * @param onImageReady Optional callback to call with the image element when it's ready instead of returning a data URL.
   * @returns Data URL representing the image. Undefined in case onImageReady callback is provided.
   */
  renderImageSync(
    scene: SSFXRendererScene,
    camera: THREE.Camera,
    renderParams: RendererParams,
    options: Partial<RenderImageOptions>,
    onImageReady?: ImageReadyCallback,
  ): string | undefined {
    const imageGenerator = this.renderImageInternal(scene, camera, renderParams, options);
    let genReturn: IteratorResult<any, any>;
    do {
      genReturn = imageGenerator.next();
    } while (!genReturn.done);

    const { width, height } = this.offScreenRenderTarget.getFinalRenderSize();

    if (this.onScreenRenderingDisabled) {
      // We can use the default framebuffer as a scratch buffer.
      if (this.renderer.domElement.width !== width || this.renderer.domElement.height !== height) {
        this.renderer.setSize(width, height, false);
      }
      this.offScreenRenderTarget.renderSSAAOutput(null);
      if (onImageReady !== undefined) {
        onImageReady(this.renderer.domElement);
        return undefined;
      } else {
        return this.renderer.domElement.toDataURL();
      }
    }

    const finalRenderTarget = this.renderTargetPool.reserveRenderTarget({
      width,
      height,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false,
      colorSpace: THREE.LinearSRGBColorSpace, // We actually want SRGB encoded values, but readPixels doesn't return them even if we set the FBO to SRGBColorSpace :(
      samples: 0,
    });
    this.offScreenRenderTarget.renderSSAAOutput(finalRenderTarget);

    const readBuffer = new Uint8ClampedArray(width * height * 4);
    this.renderer.readRenderTargetPixels(finalRenderTarget, 0, 0, width, height, readBuffer);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    let dataURL;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) {
      function linearToSRGB(linear: number): number {
        linear = linear / 255;
        const srgb = linear < 0.0031308 ? linear * 12.92 : 1.055 * Math.pow(linear, 0.41666) - 0.055;
        return Math.round(srgb * 255);
      }
      // Flip readBuffer along the Y axis and convert linear values to SRGB.
      for (let y = 0; y < height / 2; ++y) {
        for (let x = 0; x < width; ++x) {
          const i1 = (y * width + x) * 4;
          const i2 = ((height - 1 - y) * width + x) * 4;
          let temp = linearToSRGB(readBuffer[i1]);
          readBuffer[i1] = linearToSRGB(readBuffer[i2]);
          readBuffer[i2] = temp;
          temp = linearToSRGB(readBuffer[i1 + 1]);
          readBuffer[i1 + 1] = linearToSRGB(readBuffer[i2 + 1]);
          readBuffer[i2 + 1] = temp;
          temp = linearToSRGB(readBuffer[i1 + 2]);
          readBuffer[i1 + 2] = linearToSRGB(readBuffer[i2 + 2]);
          readBuffer[i2 + 2] = temp;

          // Alpha channel is not converted to SRGB.
          temp = readBuffer[i1 + 3];
          readBuffer[i1 + 3] = readBuffer[i2 + 3];
          readBuffer[i2 + 3] = temp;
        }
      }
      const imageData = new ImageData(readBuffer, width, height);
      ctx.putImageData(imageData, 0, 0);
    }
    if (onImageReady !== undefined) {
      onImageReady(canvas);
    } else {
      dataURL = canvas.toDataURL();
    }

    this.renderTargetPool.freeRenderTarget(finalRenderTarget);

    return dataURL;
  }

  renderImageSyncTest(
    scene: SSFXRendererScene,
    camera: THREE.Camera,
    renderParams: RendererParams,
    options: Partial<RenderImageOptions>,
  ) {
    const imageGenerator = this.renderImageInternal(scene, camera, renderParams, options);
    let genReturn: IteratorResult<any, any>;
    do {
      genReturn = imageGenerator.next();
    } while (!genReturn.done);

    const { width, height } = this.offScreenRenderTarget.getFinalRenderSize();
    this.renderer.setSize(width, height, false);
    this.renderer.setScissor(0, 0, width, height);
    this.offScreenRenderTarget.renderSSAAOutput(null);
  }

  private *renderImageInternal(
    scene: SSFXRendererScene,
    camera: THREE.Camera,
    renderParams: RendererParams,
    options: Partial<RenderImageOptions>,
  ) {
    const defaultOptions: RenderImageOptions = {
      width: RENDER_IMAGE_DEFAULT_WIDTH,
      height: RENDER_IMAGE_DEFAULT_WIDTH,
      ssaaSamples: 4,
    };

    const usedOptions: RenderImageOptions = { ...defaultOptions, ...options };

    if (usedOptions.updateLightProbesBeforeCapture) {
      scene.updateLightProbesList();
      scene.dirtyAllLightProbes();

      const lightProbeGenerationParams = {
        ...defaultLightProbeGenerationParams,
        lightProbeFar: (camera as THREE.PerspectiveCamera).far,
      };

      for (let step = 0; step < MAX_LIGHT_PROBE_RENDER_STEPS; ++step) {
        scene.lightProbeRenderStep(this.renderer, lightProbeGenerationParams, false);
        if (!scene.hasDirtyLightProbes() && !scene.isRenderingLightProbe) {
          break;
        }
        if (step === MAX_LIGHT_PROBE_RENDER_STEPS - 1) {
          throw new Error("Light probe update loop did not finish within the maximum step count");
        }
        yield;
      }
    }

    this.offScreenRenderTarget.applyRenderParams(renderParams);

    // Render and save image.
    usedOptions.ssaaSamples = Math.min(usedOptions.ssaaSamples ?? 4, 256);
    this.offScreenRenderTarget.setScene(scene);
    this.offScreenRenderTarget.setCamera(camera);
    this.offScreenRenderTarget.setFinalRenderSize(usedOptions.width, usedOptions.height);
    this.offScreenRenderTarget.beginOffscreenFrame(Math.ceil(Math.log2(usedOptions.ssaaSamples)));

    scene.updateShadowMap(this.renderer, camera);

    let sampleCount = 0;

    do {
      prepareMaterialsForRender(options.highlightDataTexture);
      this.offScreenRenderTarget.setHighlightDataTexture(options.highlightDataTexture);
      this.offScreenRenderTarget.accumulateSampleIfNeeded();
      ++sampleCount;
      yield;
    } while (sampleCount < usedOptions.ssaaSamples);
  }

  dispose() {
    this.offScreenRenderTarget.dispose();
    this.renderTargetPool.dispose();
  }
}
